import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AdminAPI } from '../../shared/api/admin-api';
import { useNotification } from '../../shared/contexts/NotificationContext';
import PromotionForm from './PromotionForm';
import { getMainImage } from '../../../utils/image-utils';
import AnimatedList from '../../shared/components/AnimatedList';
import AnimatedItem from '../../shared/components/AnimatedItem';
import LoadingSpinner from '../../shared/components/LoadingSpinner';

interface Promotion {
  id: number;
  title: string;
  description?: string;
  discount_percent?: number;
  discount_value?: number;
  start_date?: string;
  end_date?: string;
  active: boolean;
  image?: string;
}

function Promocoes() {
  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { success, error } = useNotification();

  // Verificar se há parâmetros de edição/criação
  const isEdit = searchParams.get('edit') !== null;
  const isNew = searchParams.get('new') === 'true';

  useEffect(() => {
    // Só carregar promoções se não estiver editando/criando
    if (!isEdit && !isNew) {
      loadPromotions();
    }
  }, [isEdit, isNew]);

  const loadPromotions = async () => {
    try {
      const promotionsData = await AdminAPI.get('promotions') as Promotion[];
      setPromotions(promotionsData);
    } catch (err) {
      console.error('Erro ao carregar promoções:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (promotionId: number) => {
    navigate(`/admin/promocoes?edit=${promotionId}`);
  };

  const handleDelete = async (promotionId: number) => {
    if (!confirm('Tem certeza que deseja excluir esta promoção?')) return;
    
    try {
      await AdminAPI.delete(`promotions/${promotionId}`);
      success('Promoção excluída com sucesso!');
      await loadPromotions();
    } catch (err: any) {
      console.error('Erro ao excluir promoção:', err);
      error(err.message || 'Erro ao excluir promoção');
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };

  const isActive = (promotion: Promotion) => {
    if (!promotion.active) return false;
    const now = new Date();
    const startDate = promotion.start_date ? new Date(promotion.start_date) : null;
    const endDate = promotion.end_date ? new Date(promotion.end_date) : null;
    
    if (startDate && now < startDate) return false;
    if (endDate && now > endDate) return false;
    return true;
  };

  // Se estiver editando ou criando, mostrar o formulário (após todos os hooks)
  if (isEdit || isNew) {
    return (
      <div className="form-transition form-transition-entered">
        <PromotionForm />
      </div>
    );
  }

  return (
    <>
      {/* Loading Spinner - aparece apenas durante carregamento inicial */}
      {loading && <LoadingSpinner message="Carregando promoções..." />}
      
      {/* Filters Section */}
      <section className="filters-section">
        <div className="filter-buttons">
          <button
            className="filter-btn active"
            onClick={() => {}}
          >
            Todas
          </button>
        </div>
      </section>

      {/* Promotions List */}
      <section className="products-section">
        <AnimatedList
          listKey="promotions"
          className="products-list"
        >
          {promotions.length === 0 ? (
            <div>Nenhuma promoção encontrada</div>
          ) : (
            promotions.map((promotion, index) => {
              // Verificar se image é um ícone (começa com "fa-") ou imagem
              const imageValue = promotion.image || '';
              const isIcon = typeof imageValue === 'string' && imageValue.startsWith('fa-');
              const isImage = imageValue && !isIcon;
              const active = isActive(promotion);
              
              return (
                <AnimatedItem
                  key={promotion.id}
                  className="product-item"
                  index={index}
                >
                  <div className="product-image-container">
                    {isIcon ? (
                      <div className="product-icon-large">
                        <i className={`fas ${imageValue}`}></i>
                      </div>
                    ) : isImage ? (
                      <img
                        src={imageValue}
                        alt={promotion.title}
                        className="product-image"
                        onError={(e) => {
                          (e.target as HTMLImageElement).src = '/assets/images/placeholder.jpg';
                        }}
                      />
                    ) : (
                      <div className="product-icon-large">
                        <i className="fas fa-tag"></i>
                      </div>
                    )}
                    {active && (
                      <div className="promotion-badge-active">Ativa</div>
                    )}
                  </div>
                  <div className="product-info">
                    <div className="product-name">{promotion.title}</div>
                    {promotion.description && (
                      <div className="product-description" style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                        {promotion.description}
                      </div>
                    )}
                    <div className="product-details">
                      {promotion.discount_percent && (
                        <span className="product-price" style={{ color: 'var(--success)', fontWeight: 700 }}>
                          {promotion.discount_percent}% OFF
                        </span>
                      )}
                      {promotion.discount_value && (
                        <span className="product-price" style={{ color: 'var(--success)', fontWeight: 700 }}>
                          R$ {parseFloat(String(promotion.discount_value)).toFixed(2).replace('.', ',')} OFF
                        </span>
                      )}
                    </div>
                    <div className="product-details" style={{ marginTop: '8px', fontSize: '12px', color: 'var(--text-secondary)' }}>
                      {promotion.start_date && (
                        <span>Início: {formatDate(promotion.start_date)}</span>
                      )}
                      {promotion.end_date && (
                        <span style={{ marginLeft: '12px' }}>Fim: {formatDate(promotion.end_date)}</span>
                      )}
                    </div>
                    <div className={`product-status ${active ? 'active' : 'inactive'}`}>
                      {active ? 'Ativa' : 'Inativa'}
                    </div>
                  </div>
                  <div className="product-actions">
                    <button
                      className="action-btn edit"
                      onClick={() => handleEdit(promotion.id)}
                      title="Editar"
                    >
                      <i className="fas fa-edit"></i>
                    </button>
                    <button
                      className="action-btn delete"
                      onClick={() => handleDelete(promotion.id)}
                      title="Excluir"
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </AnimatedItem>
              );
            })
          )}
        </AnimatedList>
      </section>
    </>
  );
}

export default Promocoes;

